import { SchemaType } from '@/constants/schema'

const safeArray = (v) => (Array.isArray(v) ? v : [])

const schemaKey = (s) => {
  if (!s) return null
  if (s.id) return `id:${s.id}`
  // fallback for older data that might not have schema ids
  return `name:${s.type || ''}:${(s.name || '').toLowerCase()}`
}

const fieldNames = (fields) =>
  safeArray(fields)
    .map((f) => f?.name)
    .filter(Boolean)

const sameSet = (a, b) => {
  if (a.length !== b.length) return false
  const s = new Set(a)
  for (const v of b) {
    if (!s.has(v)) return false
  }
  return true
}

const firstDifference = (a, b) => {
  const len = Math.max(a.length, b.length)
  for (let i = 0; i < len; i++) {
    if (a[i] !== b[i]) return i
  }
  return -1
}

export const buildSingleSchemaUpdateNote = (oldSchema, newSchema) => {
  const schemaType = newSchema?.type || oldSchema?.type
  const schemaNoun = schemaType === SchemaType.ENTITY ? 'entity' : 'schema'
  const schemaName = newSchema?.name || oldSchema?.name || ''

  // Schema rename (entities are schemas too)
  if ((oldSchema?.name || '') !== (newSchema?.name || '')) {
    const from = oldSchema?.name || ''
    const to = newSchema?.name || ''
    return { action: `renamed ${schemaNoun}`, target: `${from} → ${to}` }
  }

  const oldFields = safeArray(oldSchema?.fields)
  const newFields = safeArray(newSchema?.fields)

  const oldNames = fieldNames(oldFields)
  const newNames = fieldNames(newFields)

  // Reorder detection (same members, different order)
  if (sameSet(oldNames, newNames) && firstDifference(oldNames, newNames) !== -1) {
    return { action: `reordered ${schemaNoun} fields`, target: schemaName }
  }

  const oldSet = new Set(oldNames)
  const newSet = new Set(newNames)

  const added = newNames.filter((n) => !oldSet.has(n))
  const removed = oldNames.filter((n) => !newSet.has(n))

  // Field add/remove
  if (added.length === 1 && removed.length === 0) {
    return { action: 'added field', target: `${schemaName}.${added[0]}` }
  }
  if (removed.length === 1 && added.length === 0) {
    return { action: 'deleted field', target: `${schemaName}.${removed[0]}` }
  }

  // Field rename heuristic: one removed + one added at same index
  if (added.length === 1 && removed.length === 1 && oldFields.length === newFields.length) {
    const fromName = removed[0]
    const toName = added[0]
    const renameIndex = oldFields.findIndex((f, idx) => f?.name === fromName && newFields[idx]?.name === toName)
    if (renameIndex !== -1) {
      return { action: 'renamed field', target: `${schemaName}.${fromName} → ${schemaName}.${toName}` }
    }
  }

  // Sub-field add/remove/rename
  for (const name of newNames) {
    if (!oldSet.has(name)) continue
    const oldField = oldFields.find((f) => f?.name === name)
    const newField = newFields.find((f) => f?.name === name)
    const oldSubs = safeArray(oldField?.fields)
    const newSubs = safeArray(newField?.fields)

    const oldSubNames = fieldNames(oldSubs)
    const newSubNames = fieldNames(newSubs)

    // Sub-field reorder
    if (sameSet(oldSubNames, newSubNames) && firstDifference(oldSubNames, newSubNames) !== -1) {
      return { action: 'reordered sub fields', target: `${schemaName}.${name}` }
    }

    const oldSubSet = new Set(oldSubNames)
    const newSubSet = new Set(newSubNames)

    const subAdded = newSubNames.filter((n) => !oldSubSet.has(n))
    const subRemoved = oldSubNames.filter((n) => !newSubSet.has(n))

    if (subAdded.length === 1 && subRemoved.length === 0) {
      return { action: 'added sub field', target: `${schemaName}.${name}.${subAdded[0]}` }
    }
    if (subRemoved.length === 1 && subAdded.length === 0) {
      return { action: 'deleted sub field', target: `${schemaName}.${name}.${subRemoved[0]}` }
    }

    if (subAdded.length === 1 && subRemoved.length === 1 && oldSubs.length === newSubs.length) {
      const fromName = subRemoved[0]
      const toName = subAdded[0]
      const renameIndex = oldSubs.findIndex((sf, i) => sf?.name === fromName && newSubs[i]?.name === toName)
      if (renameIndex !== -1) {
        return {
          action: 'renamed sub field',
          target: `${schemaName}.${name}.${fromName} → ${schemaName}.${name}.${toName}`
        }
      }
    }
  }

  // Fallback: something changed but we couldn't classify cheaply
  return { action: `updated ${schemaNoun}`, target: schemaName }
}

export const buildSchemasUpdateNote = (oldSchemas, newSchemas) => {
  const oldList = safeArray(oldSchemas)
  const newList = safeArray(newSchemas)

  const oldKeySet = new Set(oldList.map(schemaKey).filter(Boolean))
  const newKeySet = new Set(newList.map(schemaKey).filter(Boolean))

  // Added schema
  const addedSchema = newList.find((s) => {
    const k = schemaKey(s)
    return k && !oldKeySet.has(k)
  })
  if (addedSchema) {
    const schemaNoun = addedSchema.type === SchemaType.ENTITY ? 'entity' : 'schema'
    return { action: `added ${schemaNoun}`, target: addedSchema.name || '' }
  }

  // Removed schema
  const removedSchema = oldList.find((s) => {
    const k = schemaKey(s)
    return k && !newKeySet.has(k)
  })
  if (removedSchema) {
    const schemaNoun = removedSchema.type === SchemaType.ENTITY ? 'entity' : 'schema'
    return { action: `deleted ${schemaNoun}`, target: removedSchema.name || '' }
  }

  // Find the first schema that differs (by key) and build a detailed note for it
  const oldByKey = new Map(oldList.map((s) => [schemaKey(s), s]).filter(([k]) => k))

  for (const s of newList) {
    const k = schemaKey(s)
    if (!k) continue
    const old = oldByKey.get(k)
    if (!old) continue

    // Cheap change detection: compare counts + ordered names + simple top-level props
    const oldName = old?.name || ''
    const newName = s?.name || ''
    if (oldName !== newName) return buildSingleSchemaUpdateNote(old, s)

    if ((old?.description || '') !== (s?.description || '')) {
      const schemaNoun = s.type === SchemaType.ENTITY ? 'entity' : 'schema'
      return { action: `updated ${schemaNoun} description`, target: newName }
    }

    const oldFields = fieldNames(old?.fields)
    const newFields = fieldNames(s?.fields)
    if (oldFields.join('|') !== newFields.join('|')) return buildSingleSchemaUpdateNote(old, s)

    // Check nested field name lists (subfields)
    const oldFieldObjs = safeArray(old?.fields)
    const newFieldObjs = safeArray(s?.fields)
    for (let i = 0; i < Math.min(oldFieldObjs.length, newFieldObjs.length); i++) {
      const of = oldFieldObjs[i]
      const nf = newFieldObjs[i]
      if ((of?.name || '') !== (nf?.name || '')) {
        return buildSingleSchemaUpdateNote(old, s)
      }
      const oldSubs = fieldNames(of?.fields)
      const newSubs = fieldNames(nf?.fields)
      if (oldSubs.join('|') !== newSubs.join('|')) {
        return buildSingleSchemaUpdateNote(old, s)
      }
    }
  }

  return { action: 'updated schema', target: '' }
}
